<template>
    <div class="ui-page">
        <ui-page-header :title="page.title" :back="`/agcs/${dataset.generalMeetingId}`" back-text="Detalhes da Assembleia">
            <template #subtitle>
                <span v-if="dataset.data.assemblyName">Assembleia: {{ dataset.data.assemblyName }}</span>
                <span v-if="dataset.data.assemblyName && dataset.data.clientName"> · </span>
                <span v-if="dataset.data.clientName">Caso: {{ dataset.data.clientName }}</span>
            </template>
            <v-btn type="button" color="primary" outlined :disabled="dataList.loading" @click="generateCSV">
                <v-icon left small>mdi-file-excel-outline</v-icon>
                Exportar Excel
            </v-btn>
            <v-btn v-if="(hasProcuratorFilter) && $allowed('edit')" type="button" color="primary" outlined :loading="dataForm.bulkPresenceLoading"
                :disabled="dataList.loading" @click="presenceByProcurator">
                <v-icon left small>mdi-account-multiple-check-outline</v-icon>
                Marcar representados
            </v-btn>
            <v-btn type="button" color="primary" outlined :disabled="dataList.loading" @click="openBatchSelection">
                <v-icon left small>mdi-account-switch-outline</v-icon>
                Substabelecer em lote
            </v-btn>
            <v-btn v-if="$allowed('create')" id="btnAddCreditor" type="button" color="primary" depressed @click="openManualCreditorDialog">
                <v-icon left small>mdi-account-plus-outline</v-icon>
                Incluir credor
            </v-btn>
        </ui-page-header>
        <!-- <v-row>
            <v-col>
                <span class="text-uppercase"> <strong>{{ dataset.data.name }}</strong></span>
            </v-col>
        </v-row> -->

        <v-alert v-if="blockedCreditors.length" dense text type="warning" class="mb-4">
            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
                <span>
                    <strong>{{ blockedCreditors.length }} credor(es) impedido(s) nesta sessão</strong> por ausência em sessão anterior.
                    Eles não constam da lista abaixo e não podem se credenciar, votar nem entrar na reunião.
                </span>
                <v-btn small text color="warning" @click="showBlocked = !showBlocked">
                    {{ showBlocked ? 'Ocultar' : 'Ver impedidos' }}
                </v-btn>
            </div>
            <v-simple-table v-if="showBlocked" dense class="mt-2">
                <thead>
                    <tr><th>Credor</th><th>Classe</th><th>Sessão da ausência</th><th>Motivo</th></tr>
                </thead>
                <tbody>
                    <tr v-for="item in blockedCreditors" :key="item.id">
                        <td>{{ item.name }}</td>
                        <td>{{ item.class_id | enum(dataset.creditorTypes || [], 'id', 'name') }}</td>
                        <td>{{ item.absent_session.name }} <span class="ui-muted">({{ item.absent_session.date | defaultDate('DD/MM/YYYY') }})</span></td>
                        <td class="text-caption">{{ item.blocked_reason }}</td>
                    </tr>
                </tbody>
            </v-simple-table>
        </v-alert>

        <div class="ui-list-bar">
            <ui-pills v-model="dataset.filter.presence" :items="presencePills" @input="applyFilters" />
        </div>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field id="name" v-model="dataset.filter.name" placeholder="Buscar pelo nome" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field id="procurator" v-model="dataset.filter.procurator" placeholder="Procurador"
                    dense outlined hide-details class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-select :items="dataset.creditorTypes" v-model="dataset.filter.type" item-value="id" item-text="name"
                    placeholder="Todas as classes" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters">
                </v-select>
            </template>

            <v-data-table :headers="presenceHeaders" :items="dataList.data" :server-items-length="dataList.count"
                :options.sync="dataList.options" :loading="dataList.loading" disable-sort hide-default-footer
                no-data-text="Nenhuma lista de credores foi encontrada" loading-text="Carregando dados...">

                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div class="ui-cell-sub">
                        {{ formatRegistrationValue(item.registration) }}
                        <v-tooltip v-if="item.registration_fictitious" bottom>
                            <template #activator="{ on }">
                                <v-icon v-on="on" x-small color="warning" class="ml-1">mdi-alert-circle</v-icon>
                            </template>
                            <span>CPF/CNPJ fictício: regularize o documento deste credor.</span>
                        </v-tooltip>
                    </div>
                </template>

                <template v-slot:[`item.procurator_name`]="{ item }">
                    <div v-if="item.has_procurator">
                        <div class="ui-cell-main">{{ item.procurator_name || 'N/A' }}</div>
                        <div class="ui-cell-sub">{{ item.procurator_email || 'Sem e-mail' }}</div>
                        <div class="ui-cell-sub" v-if="item.procurator_registration">
                            {{ formatRegistrationValue(item.procurator_registration) }}
                        </div>
                    </div>
                    <span v-else class="ui-muted">N/A</span>
                </template>

                <template v-slot:[`item.type`]="{ item }">
                    {{ item.type | enum(dataset.creditorTypes, 'id', 'name') }}
                </template>

                <template v-slot:[`item.sessions`]="{ item }">
                    <div class="d-flex flex-wrap" style="gap: 4px;">
                        <ui-badge v-for="session in item.sessions || []" :key="session.id" :title="session.name"
                            :tone="session.present === null ? 'neutral' : (session.present ? 'success' : 'danger')"
                            :text="`${session.label}: ${session.present === null ? (session.recorded ? 'não se aplica' : 'sem registro') : (session.present ? 'presente' : 'ausente')}`" />
                    </div>
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="item.presence ? 'SIM' : 'NÃO'" :color="item.presence ? 'accent' : 'red'" />
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <!-- Troca do advogado sem sair da lista (substabelecimento registrado pela AJ) -->
                    <v-tooltip v-if="item.has_procurator && item.accreditation_general_meeting_id" bottom>
                        <template #activator="{ on, attrs }">
                            <v-btn icon small class="mr-1" v-bind="attrs" v-on="on" aria-label="Substabelecer" @click.stop="openSubstitution(item)">
                                <v-icon small>mdi-account-switch-outline</v-icon>
                            </v-btn>
                        </template>
                        <span>Substabelecer (trocar o procurador)</span>
                    </v-tooltip>
                    <!-- Marcacao em um clique: usada durante a sessao ao vivo -->
                    <v-btn v-if="item.presence" small outlined class="ui-btn-danger" @click.stop="presenceUncheck(item)">
                        <v-icon left small>mdi-account-cancel-outline</v-icon>Ausente
                    </v-btn>
                    <v-btn v-else small outlined @click.stop="presenceCheck(item)">
                        <v-icon left small>mdi-account-check-outline</v-icon>Presente
                    </v-btn>
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <!-- Pessoas fora do QGC incluidas manualmente: nao alteram o QGC, nao contam no quorum e nao votam. -->
        <ui-section v-if="external.items.length" title="Participantes fora do QGC" class="mt-4">
            <template #actions>
                <span class="ui-muted text-caption">Não constam no QGC do caso: sem quórum e sem voto</span>
            </template>
            <v-data-table :headers="external.headers" :items="external.items" :loading="external.loading"
                disable-sort disable-pagination hide-default-footer no-data-text="Nenhum participante fora do QGC">
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div class="ui-cell-sub">{{ item.registration ? formatRegistrationValue(item.registration) : 'Sem CPF/CNPJ' }}<span v-if="item.email"> · {{ item.email }}</span></div>
                    <div v-if="item.notes" class="ui-cell-sub">{{ item.notes }}</div>
                </template>
                <template v-slot:[`item.participation_label`]="{ item }">
                    <ui-badge :text="item.participation_label" tone="neutral" />
                </template>
                <template v-slot:[`item.creditor_class`]="{ item }">
                    <span v-if="item.creditor_class !== null">{{ item.creditor_class | enum(dataset.creditorTypes, 'id', 'name') }}</span>
                    <span v-else class="ui-muted">-</span>
                </template>
                <template v-slot:[`item.amount`]="{ item }">
                    <span v-if="item.amount !== null">{{ formatCurrencyValue(item.amount) }}</span>
                    <span v-else class="ui-muted">-</span>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="item.present ? 'SIM' : 'NÃO'" :color="item.present ? 'accent' : 'red'" />
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex align-center justify-end" style="gap: 4px;">
                        <v-btn v-if="item.present" small outlined class="ui-btn-danger" :disabled="!$allowed('edit')" @click.stop="setExternalPresence(item, false)">
                            <v-icon left small>mdi-account-cancel-outline</v-icon>Ausente
                        </v-btn>
                        <v-btn v-else small outlined :disabled="!$allowed('edit')" @click.stop="setExternalPresence(item, true)">
                            <v-icon left small>mdi-account-check-outline</v-icon>Presente
                        </v-btn>
                        <ui-row-actions :actions="[
                            { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => openExternalEdit(item), disabled: !$allowed('edit') },
                            { text: 'Remover da AGC', icon: 'mdi-delete-outline', handler: () => removeExternal(item), disabled: !$allowed('delete') },
                        ]" />
                    </div>
                </template>
            </v-data-table>
        </ui-section>

            <!-- <v-col cols="12" md="4">
            <v-card-title class=" d-flex justify-space-between table-card-title ">Estatística

            <div class="icon-container">
                    <v-spacer></v-spacer>
                    <v-tooltip bottom>
                        <template #activator="{ on }">
                            <v-icon icon v-on="on" @click="generatePDF()" size="large" class="me-2">
                                mdi-file-pdf-box
                            </v-icon>
                        </template>
                        <span>Gerar PDF</span>
                    </v-tooltip>
                </div> 

            </v-card-title>

                <div class="summary-item">
                    Participante(s): <span class="summary-value">{{ dataList.data.length }}</span>
                    <br>
                </div>
                <div class="summary-item">
                    SIM: <span class="summary-value color-yes">{{ yesCount }}</span> <br>
                    Percentual SIM: <span class="summary-value color-yes" >{{ yesPercentage }} %</span>
                </div>
                <div class="summary-item">
                    NÃO: <span class="summary-value color-no">{{ noCount }}</span> <br>
                    Percentual NÃO: <span class="summary-value color-no">{{ noPercentage }} %</span>
                </div>
                <div class="summary-item">
                    NULO: <span class="summary-value color-null">{{ nullCount }}</span> <br>
                    Percentual NULO: <span class="summary-value color-null">{{ nullPercentage }} %</span>
                </div>
                <div class="summary-item">
                    PENDENTE: <span class="summary-value color-pending">{{ pendingCount }}</span> <br>
                    Percentual PENDENTE: <span class="summary-value color-pending">{{ pendingPercentage
                    }} %</span>
                </div>

                </v-card>

            </v-col> -->

        <v-dialog v-model="substitution.open" max-width="720px" persistent scrollable>
            <v-card class="ui-card substitution-dialog">
                <div class="ui-card__header substitution-dialog__fixed">
                    <h3 class="ui-section-title">{{ substitution.items.length > 1 ? 'Substabelecer em lote' : 'Substabelecer' }}</h3>
                </div>
                <div v-if="substitution.items.length" class="ui-card__body substitution-dialog__body">
                    <div v-if="substitution.items.length === 1" class="substitution-dialog__current mb-4">
                        <div class="ui-cell-main">{{ substitution.items[0].name }}</div>
                        <div class="ui-cell-sub">Procurador atual: {{ substitution.items[0].procurator_name || 'N/A' }}<span v-if="substitution.items[0].procurator_email"> · {{ substitution.items[0].procurator_email }}</span></div>
                    </div>
                    <div v-else class="substitution-dialog__current d-flex align-center justify-space-between mb-4" style="gap: 12px;">
                        <div style="min-width: 0;">
                            <div class="ui-cell-main">{{ substitution.items.length }} credores selecionados</div>
                            <div class="ui-cell-sub">{{ substitutionNamesText }}</div>
                            <div class="ui-cell-sub">Procurador(es) atual(is): {{ substitutionProcuratorsText }}</div>
                        </div>
                        <v-btn v-if="$allowed('edit')" small outlined color="primary" :disabled="substitution.saving" @click="reopenBatchSelection">Alterar seleção</v-btn>
                    </div>
                    <v-radio-group v-model="substitution.form.modality" row dense hide-details class="mt-0 mb-1">
                        <v-radio label="Sem reserva de poderes" value="without_reservation" />
                        <v-radio label="Com reserva de poderes" value="with_reservation" />
                    </v-radio-group>
                    <p class="ui-small ui-muted mb-4">
                        {{ substitution.form.modality === 'without_reservation'
                            ? 'O novo procurador substitui o atual agora, nesta lista e nos relatórios. A presença dos credores não muda.'
                            : 'Os dois procuradores permanecem habilitados; o procurador da lista continua o atual.' }}
                    </p>
                    <v-row dense>
                        <v-col cols="12" md="6"><v-text-field v-model="substitution.form.new_procurator_name" outlined dense hide-details="auto" label="Nome do novo procurador *" /></v-col>
                        <v-col cols="12" md="6"><v-text-field v-model="substitution.form.new_procurator_registration" outlined dense hide-details="auto" label="CPF *" /></v-col>
                        <v-col cols="12" md="6"><v-text-field v-model="substitution.form.new_procurator_email" outlined dense hide-details="auto" label="E-mail *" /></v-col>
                        <v-col cols="12" md="6"><v-text-field v-model="substitution.form.new_procurator_phone" outlined dense hide-details="auto" label="Telefone" /></v-col>
                        <v-col cols="12" md="6"><v-text-field v-model="substitution.form.new_procurator_oab" outlined dense hide-details="auto" label="OAB (se advogado)" /></v-col>
                        <v-col cols="12" md="6"><v-select v-model="substitution.form.new_procurator_oab_state" :items="ufs" outlined dense hide-details="auto" clearable label="UF da OAB" /></v-col>
                        <v-col cols="12"><v-textarea v-model="substitution.form.reason" outlined dense hide-details rows="2" auto-grow label="Observação (opcional)" /></v-col>
                    </v-row>
                    <p class="ui-small ui-muted mt-3 mb-0">
                        Fica registrado como substabelecimento aprovado pela Administração Judicial, com protocolo<span v-if="substitution.items.length > 1"> (um por credor)</span>, e aparece em Substabelecimentos e na área do procurador.
                    </p>
                </div>
                <div class="ui-card__footer substitution-dialog__fixed">
                    <v-btn text :disabled="substitution.saving" @click="substitution.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('gm.substitutions')" color="primary" depressed :loading="substitution.saving" :disabled="!canSaveSubstitution" @click="saveSubstitution">Salvar substabelecimento</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Lote: escolher os credores (todos os da AGC com procurador), com busca e filtro pelo procurador atual -->
        <v-dialog v-model="batchSelection.open" max-width="680px" persistent scrollable>
            <v-card class="ui-card substitution-dialog">
                <div class="ui-card__header substitution-dialog__fixed d-flex align-center">
                    <h3 class="ui-section-title">Selecionar credores para substabelecer</h3>
                    <v-spacer />
                    <span class="ui-small ui-muted">{{ batchSelection.draft.length }} de {{ batchSelection.rows.length }} selecionados</span>
                </div>
                <div class="ui-card__body substitution-dialog__body">
                    <v-row dense class="mb-1">
                        <v-col cols="12" md="6"><v-text-field v-model="batchSelection.search" prepend-inner-icon="mdi-magnify" placeholder="Buscar credor" outlined dense hide-details clearable /></v-col>
                        <v-col cols="12" md="6"><v-autocomplete v-model="batchSelection.procurator" :items="batchProcurators" outlined dense hide-details clearable placeholder="Procurador atual (todos)" /></v-col>
                    </v-row>
                    <v-progress-linear v-if="batchSelection.loading" indeterminate class="my-2" />
                    <template v-else>
                        <v-checkbox :input-value="allBatchFilteredSelected" :indeterminate="someBatchFilteredSelected" :disabled="!batchFilteredRows.length"
                            :label="batchSelection.search || batchSelection.procurator ? 'Selecionar todos os filtrados' : 'Selecionar todos'" dense hide-details class="mt-1 mb-2" @change="toggleBatchFiltered" />
                        <v-divider />
                        <div v-if="!batchFilteredRows.length" class="ui-small ui-muted py-4 text-center">Nenhum credor com procurador encontrado.</div>
                        <v-checkbox v-for="row in batchFilteredRows" :key="`batch-${row.accreditation_general_meeting_id}`" v-model="batchSelection.draft" :value="row.accreditation_general_meeting_id" dense hide-details class="mt-2">
                            <template #label>
                                <div>
                                    <div>{{ row.name }}</div>
                                    <div class="ui-small ui-muted">
                                        Procurador: {{ row.procurator_name || 'N/A' }}
                                        <span v-if="row.class_labels.length"> · {{ row.class_labels.join(' · ') }}</span>
                                    </div>
                                </div>
                            </template>
                        </v-checkbox>
                    </template>
                </div>
                <div class="ui-card__footer substitution-dialog__fixed">
                    <v-btn text :disabled="!batchSelection.draft.length" @click="batchSelection.draft = []">Limpar</v-btn>
                    <v-spacer />
                    <v-btn text @click="batchSelection.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :disabled="!batchSelection.draft.length" @click="confirmBatchSelection">Continuar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <v-dialog v-model="manualCreditorDialog.open" max-width="760px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">{{ manualCreditorDialog.editingId ? 'Editar participante fora do QGC' : 'Incluir credor na AGC' }}</h3>
                </div>
                <div class="ui-card__body">
                    <v-btn-toggle v-if="!manualCreditorDialog.editingId" v-model="manualCreditorDialog.form.source" mandatory dense color="primary" class="mb-4">
                        <v-btn small value="existing">Credor do QGC</v-btn>
                        <v-btn small value="external">Pessoa fora do QGC</v-btn>
                    </v-btn-toggle>

                    <template v-if="manualCreditorDialog.form.source === 'existing'">
                        <v-alert type="info" text dense>Selecione um credor do caso para registrar sua participação. Para créditos exclusivos da AGC, utilize um cenário.</v-alert>

                        <v-autocomplete
                            v-model="manualCreditorDialog.form.creditor"
                            :items="manualCreditorDialog.creditors"
                            :item-text="formatCreditorOption"
                            return-object
                            dense
                            outlined
                            clearable
                            hide-details="auto"
                            label="Credor"
                            :loading="manualCreditorDialog.creditorsLoading"
                            @update:search-input="loadAvailableCreditors"
                        />
                    </template>

                    <template v-else>
                        <v-alert type="info" text dense>
                            Qualquer pessoa que não consta no QGC do caso. Ela entra na lista de presença e no relatório final,
                            sem alterar o QGC, sem contar no quórum de instalação e sem voto. Para dar crédito e voto na AGC, utilize um cenário.
                        </v-alert>
                        <v-form ref="externalForm" @submit.prevent>
                            <v-row dense>
                                <v-col cols="12" md="8">
                                    <v-text-field v-model="manualCreditorDialog.form.name" label="Nome completo *" outlined dense
                                        :rules="[(v) => !!String(v || '').trim() || 'Informe o nome']" />
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model="manualCreditorDialog.form.registration" label="CPF/CNPJ" outlined dense
                                        hint="Opcional" persistent-hint :rules="[externalRegistrationRule]" />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="manualCreditorDialog.form.email" label="E-mail" outlined dense
                                        :rules="[(v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim()) || 'E-mail inválido']" />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select v-model="manualCreditorDialog.form.participation_type" :items="external.participationTypes"
                                        item-value="id" item-text="name" label="Participação" outlined dense :menu-props="{ offsetY: true }" />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select v-model="manualCreditorDialog.form.creditor_class" :items="externalClassOptions"
                                        item-value="id" item-text="name" label="Classe informada" outlined dense clearable
                                        hint="Opcional e apenas informativa" persistent-hint :menu-props="{ offsetY: true }" />
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-currency-field v-model="manualCreditorDialog.form.amount" label="Valor informado" outlined dense
                                        :allow-negative="false" hint="Opcional e apenas informativo" persistent-hint />
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea v-model="manualCreditorDialog.form.notes" label="Observação" outlined dense rows="2" auto-grow
                                        hint="Ex.: habilitação pendente, cessionário, representante da devedora" persistent-hint />
                                </v-col>
                                <v-col v-if="!manualCreditorDialog.editingId" cols="12">
                                    <v-checkbox v-model="manualCreditorDialog.form.present" label="Registrar como presente" dense hide-details class="mt-0" />
                                </v-col>
                            </v-row>
                        </v-form>
                    </template>
                </div>
                <div class="ui-card__footer">
                    <v-btn text @click="closeManualCreditorDialog" :disabled="manualCreditorDialog.loading">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed @click="saveManualCreditor" :loading="manualCreditorDialog.loading">
                        Salvar
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
import { downloadXlsx } from "@/common/util/export-xlsx.util";

import Vue from "vue";
import _ from "lodash";

import presenceTypes from "@/common/data/presence-types.data"
import creditorTypes from "@/common/data/creditor-types.data"
import creditorClassUtil from "@/common/util/creditor-class.util"
import string from "@/common/util/string"

import object from "@/common/util/object"
import { cpf, cnpj } from 'cpf-cnpj-validator'

import axios from "axios"

export default {
    name: "GeneralMeetingPresence",

    data: () => ({

        page: {
            isNew: false,
            title: "",
        },
        menu: false,
        // Prosseguimento: sessoes anteriores (colunas) e impedidos por ausencia.
        sessionChain: [],
        blockedCreditors: [],
        showBlocked: false,
        dataset: {
            breadcrumbItems: [],
            date: null,
            data: {},
            clients: [],
            filter: {
                name: "",
                procurator: "",
                presence: "",
            },
            generalMeetingId: null,
            presenceTypes: presenceTypes,
            creditorTypes: [ {id: "", name: "Todos"}, ...creditorTypes],
        },
        dataForm: {
            validForm: true,
            addLoading: false,
            updateLoading: false,
            removeLoading: false,
            bulkPresenceLoading: false,
        },

        eventStatus: [
            { id: 0, name: "NÃO" },
            { id: 1, name: "SIM" },
        ],
        dataList: {
            headers: [
                { text: "Credor", value: "name" },
                { text: "Procurador", value: "procurator_name" },
                { text: "Presença", value: "status" },
                { text: "Classe", value: "type" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 170 },
            ],
            data: [],

            loading: false,
            // Declarado aqui para ser reativo: sem ele, o numero no filtro Presente/Ausente ficava com o valor anterior.
            count: 0,
            options: {
                page: 1,
                itemsPerPage: 10,
                sortBy: ['name'],
                sortDesc: [false],
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: '{0}-{1} de {2}',
            },
        },
        substitution: {
            open: false,
            saving: false,
            items: [],
            form: {},
        },
        // Lote: todos os credores da AGC com procurador; draft = credenciamentos marcados.
        batchSelection: {
            open: false,
            loading: false,
            search: '',
            procurator: null,
            rows: [],
            draft: [],
        },
        ufs: ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'],
        manualCreditorDialog: {
            open: false,
            loading: false,
            creditorsLoading: false,
            creditors: [],
            lastCreditorSearch: null,
            // Participante fora do QGC em edicao (null = inclusao).
            editingId: null,
            form: {
                source: 'existing',
                creditor: null,
                name: '',
                registration: '',
                type: 1,
                amount: 0
            }
        },
        // Pessoas fora do QGC incluidas na AGC (tabela propria, sem QGC, quorum ou voto).
        external: {
            loading: false,
            items: [],
            participationTypes: [
                { id: 1, name: 'Credor fora do QGC' },
                { id: 2, name: 'Ouvinte / interessado' },
                { id: 3, name: 'Outro' },
            ],
            headers: [
                { text: 'Participante', value: 'name' },
                { text: 'Participação', value: 'participation_label' },
                { text: 'Classe informada', value: 'creditor_class' },
                { text: 'Valor informado', value: 'amount', align: 'end' },
                { text: 'Presença', value: 'status' },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 200 },
            ],
        }
    }),

    created() {
        this.dataset.generalMeetingId = this.$route.params.id;

        this.loadData()
    },

    computed: {
        substitutionNamesText() {
            const names = this.substitution.items.map((item) => item.name);
            return names.length > 3 ? `${names.slice(0, 3).join(', ')} e mais ${names.length - 3}` : names.join(', ');
        },
        substitutionProcuratorsText() {
            return [...new Set(this.substitution.items.map((item) => item.procurator_name || 'N/A'))].join(', ');
        },
        batchProcurators() {
            return [...new Set(this.batchSelection.rows.map((row) => row.procurator_name).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
        },
        batchFilteredRows() {
            const term = String(this.batchSelection.search || '').trim().toLowerCase();
            return this.batchSelection.rows.filter((row) => (!term || String(row.name || '').toLowerCase().includes(term))
                && (!this.batchSelection.procurator || row.procurator_name === this.batchSelection.procurator));
        },
        allBatchFilteredSelected() {
            return this.batchFilteredRows.length > 0 && this.batchFilteredRows.every((row) => this.batchSelection.draft.includes(row.accreditation_general_meeting_id));
        },
        someBatchFilteredSelected() {
            return this.batchFilteredRows.some((row) => this.batchSelection.draft.includes(row.accreditation_general_meeting_id)) && !this.allBatchFilteredSelected;
        },
        canSaveSubstitution() {
            const form = this.substitution.form || {};
            return !!(String(form.new_procurator_name || '').trim() && String(form.new_procurator_registration || '').trim() && String(form.new_procurator_email || '').trim());
        },
        hasFilters() {
            return Object.values(this.dataset.filter).some((value) => value);
        },
        externalClassOptions() {
            return (this.dataset.creditorTypes || []).filter((item) => item.id !== '');
        },
        presencePills() {
            const active = this.dataset.filter.presence;
            return [
                { value: "", text: "Todos", count: active === "" ? this.dataList.count : undefined },
                { value: 1, text: "Presente", tone: "success", count: active === 1 ? this.dataList.count : undefined },
                { value: 2, text: "Ausente", tone: "danger", count: active === 2 ? this.dataList.count : undefined },
            ];
        },
        // Em prosseguimento, entra a coluna com a presenca de cada sessao anterior.
        presenceHeaders() {
            if (!this.sessionChain.length) return this.dataList.headers;
            const headers = [...this.dataList.headers];
            const idx = headers.findIndex((h) => h.value === 'status');
            headers.splice(idx < 0 ? headers.length - 1 : idx, 0, { text: 'Sessões anteriores', value: 'sessions', sortable: false });
            return headers;
        },
        computedDateFormatted() {
            return this.formatDate(this.dataset.data.date)
        },

        checkedCountComputed() {
            return this.dataList.data.filter(item => item.checked).length;
        },
        uncheckedCountComputed() {
            return this.dataList.data.filter(item => !item.checked).length;
        },
        hasProcuratorFilter() {
            return Boolean(String(this.dataset.filter.procurator || '').trim())
        },
    },

    watch: {
        'manualCreditorDialog.form.registration'(value) {
            const maskedValue = this.maskRegistration(value)

            if (value !== maskedValue) {
                this.$nextTick(() => {
                    this.manualCreditorDialog.form.registration = maskedValue
                })
            }
        }
    },

    methods: {

        applyFilters() {
            this.dataList.options.page = 1;
            this.filterSearch();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.dataset.filter = { name: "", procurator: "", presence: "", type: "" };
            this.applyFilters();
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        pageChanged(page) {
            this.dataList.options.page = page;
            this.filterSearch();
        },

        pageSizeChanged(size) {
            this.dataList.options.itemsPerPage = size;
            this.filterSearch(true);
        },

        goBack() {
            this.$router.go(-1);
        },

        async loadData() {
            this.setPage();
            this.listBreadCrumbItems();

            await this.loadAssembly();
            await this.loadCreditorClasses();
            await this.filterSearch();
            await this.loadExternalParticipants();
        },

        async loadCreditorClasses() {
            // A tela usa a lista como filtro, rotulo de tabela e select de
            // edicao ao mesmo tempo. Inclui as ocultas para que credores ja
            // classificados nelas continuem legiveis.
            const classes = await creditorClassUtil.listLabels(this.dataset.data.client_id);

            this.dataset.creditorTypes = [{ id: "", name: "Todos" }, ...classes];
        },

        setPage() {
            this.page.title = `Presença na Assembleia`;
        },

        formatDate(date) {
            if (!date) return null

            return this.$moment(date).format('DD/MM/YYYY')
        },
        parseDate(date) {
            if (!date) return null

            const [month, day, year] = date.split('/')
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
        },

        listBreadCrumbItems() {
            this.dataset.breadcrumbItems = [
                {
                    text: "Homepage",
                    disabled: false,
                    to: "/home",
                    exact: true,
                },
            ];

            this.dataset.breadcrumbItems.push({
                text: "AGC",
                disabled: false,
                to: "/agcs",
                exact: true,
            })

            this.dataset.breadcrumbItems.push({
                text: `Presença na Assembleia`,
                disabled: true,
                exact: true,
            });
        },

        getColorByStatus(status) {
            switch (status) {
                case 0:
                    return 'warning'
                case 1:
                    return 'accent'
                case 2:
                    return 'error'
                default:
                    break;
            }
        },



        async filterSearch() {

            let search = Object.fromEntries(Object.entries(this.dataset.filter).filter(([_, v]) => v))

            search = !object.isEmpty(search) ? search : {};

            //     //search.general_meeting_id = id

            //     if(!search.presence && this.dataset.filter.presence === 0) search.presence = 0

            this.dataList.loading = true;

            var limit = this.dataList.options.itemsPerPage;

            try {
                const result = await axios.get('/api/admin/general-meetings/list-presence', { params: { page: this.dataList.options.page, limit: limit, search: search, general_meeting_id: this.dataset.generalMeetingId } });

                const resultContent = result.data.content
this.dataList.data = resultContent.data
this.dataList.count = resultContent.paging.total;
this.sessionChain = resultContent.session_chain || [];
this.blockedCreditors = resultContent.blocked || [];

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }

        },

        openManualCreditorDialog() {
            this.manualCreditorDialog.editingId = null
            this.manualCreditorDialog.form = {
                source: 'existing',
                creditor: null,
                name: '',
                registration: '',
                email: '',
                participation_type: 1,
                creditor_class: null,
                amount: null,
                notes: '',
                present: true,
                type: 1
            }
            if (this.$refs.externalForm) this.$refs.externalForm.resetValidation()
            this.manualCreditorDialog.creditors = []
            this.manualCreditorDialog.lastCreditorSearch = null
            this.manualCreditorDialog.open = true
            this.loadAvailableCreditors()
        },

        closeManualCreditorDialog() {
            this.manualCreditorDialog.open = false
            this.manualCreditorDialog.editingId = null
        },

        externalRegistrationRule(value) {
            const digits = String(value || '').replace(/\D/g, '')
            if (!digits) return true
            if (digits.length === 11) return cpf.isValid(digits) || 'CPF inválido'
            if (digits.length === 14) return cnpj.isValid(digits) || 'CNPJ inválido'
            return 'Informe um CPF (11 dígitos) ou CNPJ (14 dígitos), ou deixe em branco'
        },

        async loadExternalParticipants() {
            this.external.loading = true
            try {
                const result = await axios.get(`/api/admin/general-meetings/${this.dataset.generalMeetingId}/external-participants`)
                this.external.items = result.data.content || []
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.external.loading = false
            }
        },

        openExternalEdit(item) {
            this.manualCreditorDialog.editingId = item.id
            this.manualCreditorDialog.form = {
                source: 'external',
                creditor: null,
                name: item.name,
                registration: this.maskRegistration(item.registration),
                email: item.email || '',
                participation_type: item.participation_type || 1,
                creditor_class: item.creditor_class,
                amount: item.amount,
                notes: item.notes || '',
                present: item.present,
                type: 1
            }
            this.manualCreditorDialog.open = true
        },

        buildExternalPayload() {
            const form = this.manualCreditorDialog.form
            return {
                name: String(form.name || '').trim(),
                registration: String(form.registration || '').replace(/\D/g, ''),
                email: String(form.email || '').trim(),
                participation_type: form.participation_type || 1,
                creditor_class: form.creditor_class === '' || form.creditor_class === undefined ? null : form.creditor_class,
                amount: form.amount === '' || form.amount === undefined ? null : form.amount,
                notes: String(form.notes || '').trim(),
                present: !!form.present,
            }
        },

        async saveExternalParticipant() {
            if (this.$refs.externalForm && !this.$refs.externalForm.validate()) return false
            const payload = this.buildExternalPayload()
            const base = `/api/admin/general-meetings/${this.dataset.generalMeetingId}/external-participants`
            if (this.manualCreditorDialog.editingId) {
                delete payload.present
                await axios.put(`${base}/${this.manualCreditorDialog.editingId}`, payload)
                this.$dialog.notify.info('Participante atualizado.', { position: 'top-right', timeout: 2000 })
            } else {
                await axios.post(base, payload)
                this.$dialog.notify.info('Participante incluído na AGC.', { position: 'top-right', timeout: 2000 })
            }
            return true
        },

        async setExternalPresence(item, present) {
            try {
                await axios.patch(`/api/admin/general-meetings/${this.dataset.generalMeetingId}/external-participants/${item.id}/presence`, { present })
                await this.loadExternalParticipants()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            }
        },

        async removeExternal(item) {
            const proceed = await this.$dialog.confirm({
                title: 'Remover da AGC',
                text: `Remover ${item.name} desta assembleia? O QGC do caso não é alterado.`,
                actions: { false: 'Cancelar', true: 'Remover' },
            })
            if (!proceed) return
            try {
                await axios.delete(`/api/admin/general-meetings/${this.dataset.generalMeetingId}/external-participants/${item.id}`)
                this.$dialog.notify.info('Participante removido da AGC.', { position: 'top-right', timeout: 2000 })
                await this.loadExternalParticipants()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            }
        },

        formatCreditorOption(item) {
            if (!item) return ''
            const registration = item.registration ? ` - ${this.formatRegistrationValue(item.registration)}` : ''
            return `${item.name || ''}${registration}`
        },

        formatRegistrationValue(value) {
            return this.maskRegistration(value)
        },

        maskRegistration(value) {
            const rawValue = String(value || '').replace(/\D/g, '').slice(0, 14)

            if (rawValue.length <= 11) {
                return rawValue
                    .replace(/^(\d{3})(\d)/, '$1.$2')
                    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
                    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4')
            }

            return rawValue
                .replace(/^(\d{2})(\d)/, '$1.$2')
                .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
                .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5')
        },

        async loadAvailableCreditors(searchText = '') {
            if (!this.dataset.data.client_id) return

            const normalizedSearch = String(searchText || '').trim()

            if (
                this.manualCreditorDialog.lastCreditorSearch === normalizedSearch &&
                (this.manualCreditorDialog.creditorsLoading || this.manualCreditorDialog.creditors.length > 0)
            ) {
                return
            }

            this.manualCreditorDialog.lastCreditorSearch = normalizedSearch
            this.manualCreditorDialog.creditorsLoading = true

            try {
                const search = { client_id: this.dataset.data.client_id }
                if (normalizedSearch) search.name = normalizedSearch

                const result = await axios.get('/api/admin/users-creditors', {
                    params: { page: 1, limit: 50, search }
                })

                this.manualCreditorDialog.creditors = result.data.content.data
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 })
            } finally {
                this.manualCreditorDialog.creditorsLoading = false
            }
        },

        buildManualCreditorPayload() {
            if (!this.manualCreditorDialog.form.creditor) throw new Error('Selecione um credor.')
            return {
                user_creditor_id: this.manualCreditorDialog.form.creditor.id,
                with_presence: true,
                participation_type: 1
            }
        },

        async saveManualCreditor() {
            this.manualCreditorDialog.loading = true

            try {
                if (this.manualCreditorDialog.form.source === 'external') {
                    const saved = await this.saveExternalParticipant()
                    if (saved) {
                        this.closeManualCreditorDialog()
                        await this.loadExternalParticipants()
                    }
                    return
                }

                const payload = this.buildManualCreditorPayload()

                await axios.post(`/api/admin/general-meetings/${this.dataset.generalMeetingId}/creditors`, payload)

                this.$dialog.notify.info('Credor incluído na AGC com sucesso!', { position: 'top-right', timeout: 2000 })
                this.closeManualCreditorDialog()
                await this.filterSearch()
            } catch (error) {
                this.$dialog.notify.error(error.response ? this.$codes(error) : (error.message || this.$codes(error)), { position: 'top-right', timeout: 5000 })
            } finally {
                this.manualCreditorDialog.loading = false
            }
        },

        buildPayload() {
            const data = {
                name: this.dataset.data.name,
                date: this.dataset.data.date,
                client: this.dataset.data.client_id,
                description: this.dataset.data.description,
            }

            return data;
        },

        emptySubstitutionForm() {
            return {
                modality: 'without_reservation',
                new_procurator_name: '',
                new_procurator_registration: '',
                new_procurator_email: '',
                new_procurator_phone: '',
                new_procurator_oab: '',
                new_procurator_oab_state: null,
                reason: '',
            };
        },

        openSubstitution(item) {
            this.substitution = { open: true, saving: false, items: [item], form: this.emptySubstitutionForm() };
        },

        async openBatchSelection() {
            this.batchSelection = { open: true, loading: true, search: '', procurator: null, rows: [], draft: [] };
            try {
                const result = await axios.get('/api/admin/general-meetings/list-presence', {
                    params: { page: 1, limit: 1000, search: {}, general_meeting_id: this.dataset.generalMeetingId },
                });
                const rows = (result.data.content && result.data.content.data) || [];
                // A lista de presenca tem uma linha por credito do QGC; o substabelecimento e por
                // credenciamento. Credor com creditos em classes diferentes vira uma linha so, com as classes.
                const byAccreditation = new Map();
                rows.filter((row) => row.has_procurator && row.accreditation_general_meeting_id).forEach((row) => {
                    const key = row.accreditation_general_meeting_id;
                    if (!byAccreditation.has(key)) byAccreditation.set(key, { ...row, class_labels: [] });
                    const label = this.creditorClassName(row.type);
                    const entry = byAccreditation.get(key);
                    if (label && !entry.class_labels.includes(label)) entry.class_labels.push(label);
                });
                this.batchSelection.rows = [...byAccreditation.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.batchSelection.loading = false;
            }
        },

        creditorClassName(type) {
            if (type === null || type === undefined || type === '') return null;
            const found = (this.dataset.creditorTypes || []).find((item) => item.id !== '' && Number(item.id) === Number(type));
            return found ? found.name : `Classe ${type}`;
        },

        // "Alterar selecao" a partir do formulario: volta a lista com os marcados atuais.
        reopenBatchSelection() {
            this.batchSelection.draft = this.substitution.items.map((item) => item.accreditation_general_meeting_id);
            this.batchSelection.open = true;
        },

        toggleBatchFiltered() {
            const ids = this.batchFilteredRows.map((row) => row.accreditation_general_meeting_id);
            this.batchSelection.draft = this.allBatchFilteredSelected
                ? this.batchSelection.draft.filter((id) => !ids.includes(id))
                : [...new Set([...this.batchSelection.draft, ...ids])];
        },

        confirmBatchSelection() {
            const items = this.batchSelection.rows.filter((row) => this.batchSelection.draft.includes(row.accreditation_general_meeting_id));
            if (!items.length) return;
            const form = this.substitution.open ? this.substitution.form : this.emptySubstitutionForm();
            this.substitution = { open: true, saving: false, items, form };
            this.batchSelection.open = false;
        },

        async saveSubstitution() {
            if (!this.canSaveSubstitution) return;
            const { form, items } = this.substitution;
            if (!!form.new_procurator_oab !== !!form.new_procurator_oab_state) {
                this.$dialog.notify.warning('Ao informar a OAB, preencha também a UF (e vice-versa).', { position: 'top-right', timeout: 3000 });
                return;
            }
            this.substitution.saving = true;
            try {
                const result = await axios.post('/api/admin/general-meeting-substitutions', {
                    ...form,
                    accreditation_general_meeting_ids: items.map((item) => item.accreditation_general_meeting_id),
                    origin: 'presence_list',
                });
                const created = (result.data.content && result.data.content.requests) || [];
                const text = created.length > 1
                    ? `${created.length} substabelecimentos registrados (protocolos ${created[0].protocol} a ${created[created.length - 1].protocol}).`
                    : `Substabelecimento registrado.${created[0] ? ` Protocolo ${created[0].protocol}.` : ''}`;
                this.$dialog.notify.info(text, { position: 'top-right', timeout: 5000 });
                this.substitution.open = false;
                await this.filterSearch();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 });
            } finally {
                this.substitution.saving = false;
            }
        },

        async presenceCheck(item) {

            const index = this.dataList.data.findIndex(obj => obj.id === item.id);

            try {

                await axios.post(`/api/admin/general-meetings/presence/${this.dataset.generalMeetingId}`, { user_creditor_id: item.id });

                if (index !== -1) {
                    this.dataList.data[index].presence = true;
                }

                this.$dialog.notify.info("Presença contabilizada com sucesso!", { position: "top-right", timeout: 2000 });

            } catch (error) {

                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

        async presenceByProcurator() {
            const procurator = String(this.dataset.filter.procurator || '').trim()

            if (!procurator) {
                this.$dialog.notify.error('Informe um procurador para marcar os representados.', {
                    position: 'top-right',
                    timeout: 3000
                })
                return
            }

            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja marcar presença para todos os credores representados pelo procurador filtrado?',
                title: 'Marcar representados',
                actions: {
                    false: 'Cancelar',
                    true: 'Confirmar'
                }
            })

            if (!proceedAction) return

            this.dataForm.bulkPresenceLoading = true

            try {
                const result = await axios.post(
                    `/api/admin/general-meetings/presence-by-procurator/${this.dataset.generalMeetingId}`,
                    { procurator }
                )

                const total = result.data.content.total || 0

                this.$dialog.notify.info(
                    total > 0
                        ? `${total} presença(s) marcada(s) com sucesso!`
                        : 'Nenhuma presença pendente encontrada para este procurador.',
                    { position: 'top-right', timeout: 3000 }
                )

                await this.filterSearch()
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 5000
                })
            } finally {
                this.dataForm.bulkPresenceLoading = false
            }
        },


        async presenceUncheck(item) {

            const index = this.dataList.data.findIndex(obj => obj.id === item.id);

            try {

                await axios.delete(`/api/admin/general-meetings/absence/${this.dataset.generalMeetingId}`, { data: {user_creditor_id: item.id }})

                if (index !== -1) {
                    this.dataList.data[index].presence = false;
                }

                this.$dialog.notify.info("Ausência contabilizada com sucesso!", { position: "top-right", timeout: 2000 });

            } catch (error) {

                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })

            } finally {
                this.dataList.loading = false;
            }

        },

        async loadAssembly() {

            try {
                const result = await axios.get(`/api/admin/general-meetings/${this.$route.params.id}`);

                const data = result.data.content;

                this.dataset.data = data;

                this.dataset.data.date = this.$moment(this.dataset.data.date).format("YYYY-MM-DD HH:mm")
                this.dataset.data.assemblyName = data.name
                this.dataset.data.clientName = data.client.name

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },


        async generateCSV() {
            const proceedAction = await this.$dialog.confirm({
                text: 'Deseja exportar os dados para Excel?',
                title: 'Exportar dados',
                actions: {
                    false: 'Cancelar',
                    true: 'Exportar'
                }
            })

            if (!proceedAction) return

            const data = this.dataList.data

            if (data.length == 0 && !this.external.items.length) {
                this.$dialog.notify.error('Sem dados para exportar!', {
                    position: 'top-right',
                    timeout: 2000
                })

                return
            }

            const newData = data.map(obj => ({
                'NOME CREDOR': obj.name.toUpperCase(),
                'EMAIL CREDOR': obj.email,
                'CPF/CNPJ CREDOR': this.formatCpfCnpj(obj.registration),
                'CLASSE': this.formatCreditorType(obj.type),
                'VALOR': this.formatCurrencyValue(obj.amount),
                'PRESENCA': obj.presence ? 'SIM' : 'NAO',
                'POSSUI PROCURADOR': obj.has_procurator ? 'SIM' : 'NAO',
                'NOME PROCURADOR': obj.has_procurator ? (obj.procurator_name || '') : '',
                'EMAIL PROCURADOR': obj.has_procurator ? (obj.procurator_email || '') : '',
                'CPF/CNPJ PROCURADOR': obj.has_procurator ? this.formatCpfCnpj(obj.procurator_registration) : '',
                'ORIGEM': 'QGC',
            }))

            this.external.items.forEach((obj) => newData.push({
                'NOME CREDOR': String(obj.name || '').toUpperCase(),
                'EMAIL CREDOR': obj.email || '',
                'CPF/CNPJ CREDOR': obj.registration ? this.formatCpfCnpj(obj.registration) : '',
                'CLASSE': obj.creditor_class !== null ? this.formatCreditorType(obj.creditor_class) : '',
                'VALOR': obj.amount !== null ? this.formatCurrencyValue(obj.amount) : '',
                'PRESENCA': obj.present ? 'SIM' : 'NAO',
                'POSSUI PROCURADOR': 'NAO',
                'NOME PROCURADOR': '',
                'EMAIL PROCURADOR': '',
                'CPF/CNPJ PROCURADOR': '',
                'ORIGEM': `Fora do QGC (${obj.participation_label})`,
            }))

            try {
                await downloadXlsx(newData, 'lista-votacao-' + this.dataset.data.assemblyName + '_' + this.getFormattedDate(), { sheetName: 'Presença' })

                this.$dialog.notify.info('Planilha Excel gerada com sucesso!', {
                    position: 'top-right',
                    timeout: 2000
                })
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                })
            }
        },

        getFormattedDate() {
            const currentDate = new Date();
            const day = String(currentDate.getDate()).padStart(2, '0'); 
            const month = String(currentDate.getMonth() + 1).padStart(2, '0');
            const year = currentDate.getFullYear(); 

            return `${day}-${month}-${year}`;
        },

        formatCreditorType(value) {
            const creditorType = this.dataset.creditorTypes.find(item => Number(item.id) === Number(value))
            return creditorType ? creditorType.name : ''
        },

        formatCurrencyValue(value) {
            const normalizedValue = Number(value || 0)

            return normalizedValue.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            })
        },

        formatCpfCnpj(value) {
            const normalizedValue = String(value || '').replace(/\D/g, '')

            if (!normalizedValue) return ''

            return normalizedValue.length == 14 ? cnpj.format(normalizedValue) : cpf.format(normalizedValue);
        },

    },
};
</script>

<style lang="scss">
.substitution-dialog { max-height: 90vh; display: flex; flex-direction: column; overflow: hidden; }
.substitution-dialog__fixed { flex-shrink: 0; }
.substitution-dialog__body { flex: 1 1 auto; min-height: 0; overflow-y: auto; }
.substitution-dialog__current { padding: 10px 12px; border: 1px solid #E5E7EB; border-radius: 6px; }

.access-container {
    border: 1px solid var(--v-gray-base) !important;
}

.custom-chip {
    background: var(--v-white-base) !important;
}

.title-page {
    color: var(--v-primary-base);
    // font-family: Montserrat;
    font-size: 40px;
    font-style: normal;
    font-weight: 700;
    line-height: 38px;
}

.section-info-title {
    color: var(--v-primary-base);
    // font-family: Montserrat;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
}

.section-info-value {
    // font-family: Montserrat;
    font-size: 14px;
    font-style: normal;
}

.list-card {
    max-height: calc(100vh - 400px);
    overflow-y: auto;
}

.summary-item {
    margin-bottom: 8px;
    border: 1px solid #ccc;
    padding: 8px;
    border-radius: 4px;
}

.summary-value {
    font-weight: bold;
}

.color-yes {
    color: green;
}

.color-no {
    color: red;
}

.color-null {
    color: grey;
}

.color-pending {
    color: orange;
}
</style>
